/**
 * Branch List Component (T039, US4, US7)
 *
 * Displays list of user's branches and provides management actions
 * for Enterprise tier users.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase'
import { getUserBranches, getAccountContext, setAccountContext } from '@/store/api'
import { showPurchaseBranchModal } from './purchase-branch-modal'
import { branchContextService } from '@/services/branchContextService'
import { populateDataHubBranchSelector } from '@/uploads/branch-selector'
import { checkFeatureAccess } from '@/tier/feature-gate'
// [FIX] Import cache clearing service
import { clearSummariesCache } from '@/services/localCacheService'

/**
 * Render branch list component
 */
export function renderBranchList(containerId: string): void {
  const container = document.getElementById(containerId)
  if (!container) {
    console.warn(`[BRANCH_LIST] Container #${containerId} not found`)
    return
  }

  const accountContext = getAccountContext()
  const branches = accountContext.userBranches || []
  const userBrands = accountContext.userBrands || []
  
  const brandMap = new Map(userBrands.map(b => [b.brandId, b.brandName]));
  const ownsAnyBranch = accountContext?.userBranches?.some((branch) => branch.role === 'owner') || false
  const featureAccess = checkFeatureAccess('multipleBranches')
  const canPurchase = ownsAnyBranch && featureAccess.allowed

  const headerAction = ownsAnyBranch
    ? (canPurchase
        ? `
          <button
            id="purchase-branch-btn"
            class="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Purchase Branch
          </button>
        `
        : `
          <button
            id="upgrade-for-branches-btn"
            class="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            title="Upgrade to ${featureAccess.requiredTier ? featureAccess.requiredTier.charAt(0).toUpperCase() + featureAccess.requiredTier.slice(1) : 'Enterprise'} to add additional branches"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            Upgrade to Add Branches
          </button>
        `)
    : ''

  const ownerBranchCount = branches.filter((branch) => branch.role === 'owner').length

  const listHTML = `
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-gray-900">
          My Branches
        </h2>
        ${headerAction}
      </div>

      <div class="mb-4 text-sm text-gray-600">
        ${ownsAnyBranch
          ? `
            You manage <strong>${ownerBranchCount}</strong> branch${ownerBranchCount !== 1 ? 'es' : ''}.
            ${featureAccess.allowed
              ? 'Your plan allows unlimited branches.'
              : 'Your plan allows up to 1 branch.'}
          `
          : `
            You are a member of <strong>${branches.length}</strong> branch${branches.length !== 1 ? 'es' : ''}.
            Branch creation is managed by the account owner.
          `}
      </div>

      <div class="space-y-3">
        ${branches.length > 0
            ? branches
                .map(
                  (branch) => {
                    const currentBrandId = branch.brandId;
                    const currentBrandName = currentBrandId ? (brandMap.get(currentBrandId) || 'Unknown Brand') : 'Unassigned';
                    
                    return `
                      <div class="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                        <div class="flex items-center justify-between">
                          <div>
                            <h3 class="font-semibold text-gray-900">${branch.branchName || branch.branchId}</h3>
                            <p class="text-sm text-gray-500">
                              Brand: <strong>${currentBrandName}</strong>
                            </p>
                          </div>
                          <div class="text-right">
                            <span class="inline-block px-3 py-1 rounded-full text-xs font-medium ${branch.role === 'owner'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }">
                              ${branch.role.charAt(0).toUpperCase() + branch.role.slice(1)}
                            </span>
                          </div>
                        </div>
                        <div class="mt-4 flex items-center justify-between">
                          <span class="text-xs text-gray-500">
                            ID: ${branch.branchId}
                          </span>
                          <div class="flex items-center gap-2">
                            <button
                              type="button"
                              class="assign-brand-btn text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 px-3 py-1 rounded-md"
                              data-branch-id="${branch.branchId}"
                              data-current-brand-id="${currentBrandId || ''}"
                              data-current-brand-name="${currentBrandName}"
                            >
                              Assign Brand
                            </button>
                            <button
                              type="button"
                              class="branch-rename-btn text-indigo-600 hover:text-indigo-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 px-3 py-1 rounded-md"
                              data-branch-id="${branch.branchId}"
                            >
                              Rename
                            </button>
                            ${branch.role === 'owner' ? `
                            <button
                              type="button"
                              class="branch-reset-btn text-orange-600 hover:text-orange-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 px-3 py-1 rounded-md ml-2"
                              data-branch-id="${branch.branchId}"
                              data-branch-name="${branch.branchName || branch.branchId}"
                            >
                              Reset
                            </button>
                            ` : ''}
                          </div>
                        </div>
                      </div>
                    `
                  }
                )
                .join('')
            : `
        <div class="text-center py-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
          </svg>
          <p>No branches found</p>
        </div>
      `
        }
      </div>
    </div>
  `

  container.innerHTML = listHTML

  // --- Purchase / Upgrade Listeners ---
  const purchaseBtn = container.querySelector('#purchase-branch-btn')
  const upgradeBtn = container.querySelector('#upgrade-for-branches-btn')

  if (purchaseBtn) {
    purchaseBtn.addEventListener('click', () => { showPurchaseBranchModal() })
  }
  if (upgradeBtn) {
    upgradeBtn.addEventListener('click', () => {
      import('../tier/upgrade-modal').then(({ showUpgradePrompt }) => {
        showUpgradePrompt('multipleBranches', 'enterprise')
      })
    })
  }

  // --- Assign Brand Listener ---
  const assignButtons = Array.from(container.querySelectorAll<HTMLButtonElement>('.assign-brand-btn'))
  assignButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const branchId = button.dataset.branchId
      const currentBrandId = button.dataset.currentBrandId || ''
      const currentBrandName = button.dataset.currentBrandName || 'Unassigned'
      if (!branchId) return;

      const context = getAccountContext()
      const targetBranch = context.userBranches.find((branch) => branch.branchId === branchId)
      if (!targetBranch) return;

      const { showAssignBranchModal } = await import('../brands/assign-branch-modal');

      showAssignBranchModal({
        targetBranch: {
          branchId: branchId,
          branchName: targetBranch.branchName || branchId,
          currentBrandName: currentBrandName
        },
        onSuccess: () => {
          window.location.reload();
        }
      });
    });
  });

  // --- Rename Listener ---
  const renameButtons = Array.from(container.querySelectorAll<HTMLButtonElement>('.branch-rename-btn'))
  renameButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const branchId = button.dataset.branchId
      if (!branchId) return

      const context = getAccountContext()
      const targetBranch = context.userBranches.find((branch) => branch.branchId === branchId)
      const currentName = targetBranch?.branchName?.trim() || targetBranch?.branchId || ''

      const { showRenameModal } = await import('../components/rename-modal')

      showRenameModal({
        title: 'Rename Branch',
        label: 'Branch Name',
        currentName: currentName,
        onRename: async (newName) => {
          const renameBranch = httpsCallable(functions, 'renameBranch')
          await renameBranch({ branchId, newName })
          window.location.reload()
        }
      })
    })
  })

  // --- Reset Listener ---
  const resetButtons = Array.from(container.querySelectorAll<HTMLButtonElement>('.branch-reset-btn'))
  resetButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const branchId = button.dataset.branchId
      const branchName = button.dataset.branchName
      if (!branchId) return

      const confirmed = window.confirm(
        `Are you sure you want to RESET "${branchName}"?\n\n` +
        `This will:\n` +
        `- Delete ALL Sales Data & P&L Reports for this branch.\n` +
        `- Rename the branch to "Empty Branch".\n` +
        `- Keep your branch slot active so you can upload new data.\n\n` +
        `This action CANNOT be undone.`
      )

      if (!confirmed) return

      const originalLabel = button.textContent || 'Reset'
      button.disabled = true
      button.textContent = 'Resetting...'

      try {
        const resetBranch = httpsCallable(functions, 'resetBranch')
        await resetBranch({ branchId })

        // [FIX] Clear local cache to remove "Ghost Data" immediately
        await clearSummariesCache()
        console.log('[BRANCH_LIST] Cache cleared after reset.')

        alert('Branch reset successfully.')
        window.location.reload()

      } catch (error: any) {
        console.error('[BRANCH_LIST] Reset failed:', error)
        alert(`Failed to reset branch: ${error.message}`)
        button.disabled = false
        button.textContent = originalLabel
      }
    })
  })
}

/**
 * Refresh branch list after changes
 */
export function refreshBranchList(containerId: string): void {
  renderBranchList(containerId)
}